import React, { useState, useEffect } from 'react';
import { Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import SearchableDropdown from '../common/SearchableDropdown';
import DatePicker from '../common/DatePicker';
import { purchaseInvoiceService, supplierService, productService } from '../../services';
import { inventoryService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import { Supplier, Product, PaymentDetail } from '../../types';
import { roundAmountFields, toSafeFixed } from '../../utils/numberUtils';

interface InvoiceItem {
  product_id: number;
  product_name: string;
  quantity: number;
  free_quantity: number;
  unit_price: number;
  unit_price_foreign?: number;
  mrp: number;
  gst_rate: number;
  cgst_rate: number;
  sgst_rate: number;
  igst_rate: number;
  ugst_rate: number;
  cess_rate: number;
  discount_percent: number;
  discount_amount: number;
  discount_amount_foreign?: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  ugst_amount: number;
  cess_amount: number;
  total_amount: number;
  total_amount_foreign?: number;
  total_price: number;
  batch_number: string;
  expiry_date: string;
  hsn_code: string;
  description: string;
  taxable_amount: number;
  taxable_amount_foreign?: number;
  total_tax_amount: number;
  tax_amount_foreign?: number;
  line_discount_percent: number;
  line_discount_amount: number;
}

interface PurchaseInvoiceFormProps {
  onSave: () => void;
  onCancel?: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  resetForm?: boolean;
  editData?: any;
}

const createEmptyItem = (): InvoiceItem => ({
  product_id: 0,
  product_name: '',
  quantity: 0,
  free_quantity: 0,
  unit_price: 0,
  unit_price_foreign: 0,
  mrp: 0,
  gst_rate: 0,
  cgst_rate: 0,
  sgst_rate: 0,
  igst_rate: 0,
  ugst_rate: 0,
  cess_rate: 0,
  discount_percent: 0,
  discount_amount: 0,
  discount_amount_foreign: 0,
  cgst_amount: 0,
  sgst_amount: 0,
  igst_amount: 0,
  ugst_amount: 0,
  cess_amount: 0,
  total_amount: 0,
  total_amount_foreign: 0,
  total_price: 0,
  batch_number: '',
  expiry_date: '',
  hsn_code: '',
  description: '',
  taxable_amount: 0,
  taxable_amount_foreign: 0,
  total_tax_amount: 0,
  tax_amount_foreign: 0,
  line_discount_percent: 0,
  line_discount_amount: 0
});

const PurchaseInvoiceForm: React.FC<PurchaseInvoiceFormProps> = ({ onSave, onCancel, isCollapsed, onToggleCollapse, resetForm, editData }) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [paymentTerms, setPaymentTerms] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [items, setItems] = useState<InvoiceItem[]>([createEmptyItem()]);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetail[]>([]);
  
  const generateInvoiceNumber = () => {
    const now = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 1;
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const fff = String(now.getMilliseconds()).padStart(3, '0');
    return `PINV-${tenantId}${dd}${mm}${yyyy}${hh}${min}${ss}${fff}`;
  };

  const [formData, setFormData] = useState({
    invoice_number: generateInvoiceNumber(),
    reference_number: '',
    supplier_id: '',
    supplier_name: '',
    supplier_phone: '',
    warehouse_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    status: 'POSTED',
    discount_percent: 0,
    discount_amount: 0,
    roundoff: 0,
    base_currency_id: 1,
    foreign_currency_id: null as number | null,
    exchange_rate: 1,
    payment_term_id: null as number | null,
    purchase_order_id: null as number | null,
    notes: '',
    tags: [] as string[]
  });
  
  const { showToast } = useToast();

  useEffect(() => {
    loadSuppliers();
    loadProducts();
    loadWarehouses();
  }, []);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        invoice_number: generateInvoiceNumber(),
        reference_number: '',
        supplier_id: '',
        supplier_name: '',
        supplier_phone: '',
        warehouse_id: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: new Date().toISOString().split('T')[0],
        status: 'POSTED',
        discount_percent: 0,
        discount_amount: 0,
        roundoff: 0,
        base_currency_id: 1,
        foreign_currency_id: null,
        exchange_rate: 1,
        payment_term_id: null,
        purchase_order_id: null,
        notes: '',
        tags: []
      });
      setItems([createEmptyItem()]);
      setPaymentDetails([]);
    }
  }, [resetForm]);

  useEffect(() => {
    if (editData) {
      setFormData({
        invoice_number: editData.invoice_number || '',
        reference_number: editData.reference_number || '',
        supplier_id: editData.supplier_id?.toString() || '',
        supplier_name: editData.supplier_name || '',
        supplier_phone: editData.supplier_phone || '',
        warehouse_id: editData.warehouse_id?.toString() || '',
        invoice_date: editData.invoice_date || new Date().toISOString().split('T')[0],
        due_date: editData.due_date || new Date().toISOString().split('T')[0],
        status: editData.status || 'POSTED',
        discount_percent: parseFloat(editData.overall_disc_percentage_base || editData.discount_percent || 0),
        discount_amount: parseFloat(editData.overall_disc_amount_base || editData.discount_amount_base || editData.discount_amount || 0),
        roundoff: parseFloat(editData.roundoff_base || editData.roundoff || 0),
        base_currency_id: editData.base_currency_id || 1,
        foreign_currency_id: editData.foreign_currency_id || null,
        exchange_rate: editData.exchange_rate || 1,
        payment_term_id: editData.payment_term_id || null,
        purchase_order_id: editData.purchase_order_id || null,
        notes: editData.notes || '',
        tags: editData.tags || []
      });
      
      let loadedItems: InvoiceItem[] = [];
      if (editData.items && Array.isArray(editData.items)) {
        loadedItems = editData.items;
      } else if (editData.line_items && Array.isArray(editData.line_items)) {
        loadedItems = editData.line_items;
      }
      
      // Merge loaded items with defaults and map API field names to component field names
      // API returns string values with _base suffix, need to parse to numbers
      if (loadedItems.length > 0) {
        const mergedItems = loadedItems.map((item: any) => ({
          ...createEmptyItem(),
          product_id: item.product_id || 0,
          product_name: item.product_name || '',
          quantity: parseFloat(String(item.quantity || 0)),
          free_quantity: parseFloat(String(item.free_quantity || 0)),
          unit_price: parseFloat(String(item.unit_price || item.unit_price_base || 0)),
          unit_price_foreign: parseFloat(String(item.unit_price_foreign || 0)),
          mrp: parseFloat(String(item.mrp || item.unit_price || item.unit_price_base || 0)),
          gst_rate: parseFloat(String(item.gst_rate || 0)),
          cgst_rate: parseFloat(String(item.cgst_rate || 0)),
          sgst_rate: parseFloat(String(item.sgst_rate || 0)),
          igst_rate: parseFloat(String(item.igst_rate || 0)),
          ugst_rate: parseFloat(String(item.ugst_rate || 0)),
          cess_rate: parseFloat(String(item.cess_rate || 0)),
          discount_percent: parseFloat(String(item.discount_percent || item.line_discount_percent || 0)),
          discount_amount: parseFloat(String(item.discount_amount || item.discount_amount_base || item.line_discount_amount || 0)),
          discount_amount_foreign: parseFloat(String(item.discount_amount_foreign || 0)),
          cgst_amount: parseFloat(String(item.cgst_amount || item.cgst_amount_base || 0)),
          sgst_amount: parseFloat(String(item.sgst_amount || item.sgst_amount_base || 0)),
          igst_amount: parseFloat(String(item.igst_amount || item.igst_amount_base || 0)),
          ugst_amount: parseFloat(String(item.ugst_amount || item.ugst_amount_base || 0)),
          cess_amount: parseFloat(String(item.cess_amount || item.cess_amount_base || 0)),
          total_amount: parseFloat(String(item.total_amount || item.total_amount_base || 0)),
          total_amount_foreign: parseFloat(String(item.total_amount_foreign || 0)),
          total_price: parseFloat(String(item.total_price || item.total_amount_base || 0)),
          batch_number: item.batch_number || '',
          expiry_date: item.expiry_date || '',
          hsn_code: item.hsn_code || '',
          description: item.description || '',
          taxable_amount: parseFloat(String(item.taxable_amount || item.taxable_amount_base || 0)),
          taxable_amount_foreign: parseFloat(String(item.taxable_amount_foreign || 0)),
          total_tax_amount: parseFloat(String(item.total_tax_amount || item.tax_amount_base || 0)),
          tax_amount_foreign: parseFloat(String(item.tax_amount_foreign || 0)),
          line_discount_percent: parseFloat(String(item.line_discount_percent || item.discount_percent || 0)),
          line_discount_amount: parseFloat(String(item.line_discount_amount || item.discount_amount_base || 0))
        }));
        setItems(mergedItems);
      } else {
        setItems([createEmptyItem()]);
      }
      
      if (editData.payment_details && Array.isArray(editData.payment_details)) {
        setPaymentDetails(editData.payment_details);
      } else {
        setPaymentDetails([]);
      }
    }
  }, [editData]);

  const loadSuppliers = async () => {
    try {
      const response = await supplierService.getSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      showToast('error', 'Failed to load suppliers');
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getProducts();
      setProducts(response.data);
    } catch (error) {
      showToast('error', 'Failed to load products');
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await inventoryService.getWarehouses({ per_page: 100 });
      const warehouseData = Array.isArray(response.data) ? response.data : 
                           Array.isArray(response.data?.data) ? response.data.data : 
                           Array.isArray(response) ? response : [];
      setWarehouses(warehouseData);
    } catch (error) {
      showToast('error', 'Failed to load warehouses');
      setWarehouses([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'discount_percent') {
      const discPercent = parseFloat(value) || 0;
      setFormData(prev => ({ ...prev, discount_percent: discPercent }));
    } else if (name === 'discount_amount') {
      const discAmount = parseFloat(value) || 0;
      const { subtotal } = calculateTotals();
      const discPercentage = subtotal > 0 ? (discAmount / subtotal) * 100 : 0;
      setFormData(prev => ({ ...prev, discount_amount: discAmount, discount_percent: discPercentage }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name.includes('percent') || name === 'roundoff' ? parseFloat(value) || 0 : value
      }));
    }
  };

  const handleSupplierChange = (value: string | number | (string | number)[]) => {
    const supplierId = Array.isArray(value) ? value[0] : value;
    const selectedSupplier = suppliers.find(supplier => supplier.id === Number(supplierId));
    
    setFormData(prev => ({ 
      ...prev, 
      supplier_id: supplierId as string,
      supplier_name: selectedSupplier?.name || '',
      supplier_phone: selectedSupplier?.phone || ''
    }));
  };

  const getSelectedSupplier = () => {
    return suppliers.find(supplier => supplier.id === Number(formData.supplier_id));
  };

  const handleProductChange = async (index: number, value: string | number | (string | number)[]) => {
    const productId = Array.isArray(value) ? value[0] : value;
    let product = products.find(p => p.id === Number(productId));
    
    // If product not found in current list, fetch it
    if (!product && productId) {
      try {
        const response = await inventoryService.getProducts({ search: '', per_page: 1000 });
        const foundProduct = response.data.find(p => p.id === Number(productId));
        if (foundProduct) {
          product = foundProduct;
          setProducts(prev => [...prev, foundProduct]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      }
    }
    
    const newItems = [...items];
    const gstRate = product?.gst_rate ?? 0;
    const mrpValue = product?.mrp_price ?? product?.selling_price ?? 0;
    // For purchase invoices, prioritize cost_price, if 0 then calculate 70% of MRP
    let calculatedUnitPrice = product?.cost_price ?? 0;
    if (calculatedUnitPrice === 0 && mrpValue > 0) {
      calculatedUnitPrice = Math.floor(mrpValue * 0.7 * 100) / 100;
    }
    
    newItems[index] = {
      ...newItems[index],
      product_id: Number(productId),
      product_name: product?.name || '',
      mrp: mrpValue,
      unit_price: calculatedUnitPrice,
      gst_rate: gstRate,
      cgst_rate: gstRate / 2,
      sgst_rate: gstRate / 2,
      igst_rate: 0,
      ugst_rate: 0,
      cess_rate: 0,
      discount_percent: 0,
      discount_amount: 0,
      discount_amount_foreign: 0,
      taxable_amount: calculatedUnitPrice,
      taxable_amount_foreign: calculatedUnitPrice * (formData.exchange_rate || 1),
      cgst_amount: 0,
      sgst_amount: 0,
      igst_amount: 0,
      ugst_amount: 0,
      cess_amount: 0,
      total_tax_amount: 0,
      tax_amount_foreign: 0,
      total_price: calculatedUnitPrice,
      total_amount: calculatedUnitPrice,
      total_amount_foreign: calculatedUnitPrice * (formData.exchange_rate || 1),
      unit_price_foreign: calculatedUnitPrice * (formData.exchange_rate || 1),
      hsn_code: product?.hsn_code || '',
      description: product?.description || ''
    };
    
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: number | string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const calculateItemTotal = (index: number, itemsArray: InvoiceItem[]) => {
    const item = itemsArray[index];
    const exchangeRate = formData.exchange_rate || 1;
    const baseAmount = item.unit_price * item.quantity;
    const discountAmount = baseAmount * (item.discount_percent / 100);
    const discountedAmount = baseAmount - discountAmount;
    
    // Calculate all tax components
    const cgstAmount = discountedAmount * (item.cgst_rate / 100);
    const sgstAmount = discountedAmount * (item.sgst_rate / 100);
    const igstAmount = discountedAmount * (item.igst_rate / 100);
    const ugstAmount = discountedAmount * (item.ugst_rate / 100);
    const cessAmount = discountedAmount * (item.cess_rate / 100);
    const totalTaxAmount = cgstAmount + sgstAmount + igstAmount + ugstAmount + cessAmount;
    const total = discountedAmount + totalTaxAmount;
    
    // Calculate foreign currency amounts
    const baseAmountForeign = baseAmount * exchangeRate;
    const discountAmountForeign = discountAmount * exchangeRate;
    const discountedAmountForeign = discountedAmount * exchangeRate;
    const totalTaxAmountForeign = totalTaxAmount * exchangeRate;
    const totalForeign = total * exchangeRate;
    const unitPriceForeign = item.unit_price * exchangeRate;

    itemsArray[index] = {
      ...item,
      discount_amount: discountAmount,
      discount_amount_foreign: discountAmountForeign,
      cgst_amount: cgstAmount,
      sgst_amount: sgstAmount,
      igst_amount: igstAmount,
      ugst_amount: ugstAmount,
      cess_amount: cessAmount,
      taxable_amount: discountedAmount,
      taxable_amount_foreign: discountedAmountForeign,
      total_tax_amount: totalTaxAmount,
      tax_amount_foreign: totalTaxAmountForeign,
      total_price: total,
      total_amount: total,
      total_amount_foreign: totalForeign,
      unit_price_foreign: unitPriceForeign,
      line_discount_percent: item.discount_percent,
      line_discount_amount: discountAmount
    };
    setItems([...itemsArray]);
  };

  const addItem = () => {
    setItems([...items, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const addPaymentDetail = () => {
    const newPayment: PaymentDetail = {
      line_no: paymentDetails.length + 1,
      payment_mode: 'CASH',
      account_id: 0,
      amount_base: 0,
      description: '',
      transaction_reference: ''
    };
    setPaymentDetails([...paymentDetails, newPayment]);
  };

  const removePaymentDetail = (index: number) => {
    const updatedPayments = paymentDetails.filter((_, i) => i !== index);
    // Re-number the line_no
    const renumbered = updatedPayments.map((payment, idx) => ({
      ...payment,
      line_no: idx + 1
    }));
    setPaymentDetails(renumbered);
  };

  const handlePaymentDetailChange = (index: number, field: keyof PaymentDetail, value: any) => {
    const newPayments = [...paymentDetails];
    newPayments[index] = { ...newPayments[index], [field]: value };
    
    // Clear bank_account_id if payment mode is not BANK
    if (field === 'payment_mode' && value !== 'BANK') {
      newPayments[index].bank_account_id = undefined;
    }
    
    setPaymentDetails(newPayments);
  };

  const calculateTotals = () => {
    const exchangeRate = formData.exchange_rate || 1;
    const subtotal = items.reduce((sum, item) => sum + (item.total_amount || 0), 0);
    const taxAmount = items.reduce((sum, item) => sum + (item.total_tax_amount || 0), 0);
    const discountAmount = formData.discount_amount || subtotal * (formData.discount_percent / 100);
    const finalTotal = subtotal - discountAmount;
    
    // Calculate foreign currency amounts
    const subtotalForeign = subtotal * exchangeRate;
    const taxAmountForeign = taxAmount * exchangeRate;
    const discountAmountForeign = discountAmount * exchangeRate;
    const finalTotalForeign = finalTotal * exchangeRate;
    
    return { 
      subtotal, 
      discountAmount, 
      finalTotal,
      subtotalForeign,
      taxAmountForeign,
      discountAmountForeign,
      finalTotalForeign
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      showToast('error', 'Please select a supplier');
      return;
    }

    const validItems = items.filter(item => item.product_id > 0 && item.quantity > 0);
    if (validItems.length === 0) {
      showToast('error', 'Please add at least one item');
      return;
    }

    try {
      const { finalTotal, discountAmount, subtotal, subtotalForeign, taxAmountForeign, discountAmountForeign, finalTotalForeign } = calculateTotals();
      const exchangeRate = formData.exchange_rate || 1;
      
      // Calculate total tax amounts (base currency)
      const cgstTotal = validItems.reduce((sum, item) => sum + item.cgst_amount, 0);
      const sgstTotal = validItems.reduce((sum, item) => sum + item.sgst_amount, 0);
      const igstTotal = validItems.reduce((sum, item) => sum + item.igst_amount, 0);
      const ugstTotal = validItems.reduce((sum, item) => sum + item.ugst_amount, 0);
      const cessTotal = validItems.reduce((sum, item) => sum + item.cess_amount, 0);
      const taxTotal = validItems.reduce((sum, item) => sum + item.total_tax_amount, 0);
      const taxableTotal = validItems.reduce((sum, item) => sum + item.taxable_amount, 0);

      // Calculate paid and balance amounts
      const paidAmount = paymentDetails.reduce((sum, payment) => sum + payment.amount_base, 0);
      const balanceAmount = finalTotal - paidAmount;

      const invoiceData = {
        invoice_number: formData.invoice_number,
        reference_number: formData.reference_number || undefined,
        supplier_id: Number(formData.supplier_id),
        supplier_name: formData.supplier_name,
        supplier_phone: formData.supplier_phone,
        warehouse_id: formData.warehouse_id ? Number(formData.warehouse_id) : 1,
        invoice_date: formData.invoice_date,
        due_date: formData.due_date || undefined,
        status: formData.status,
        base_currency_id: formData.base_currency_id,
        foreign_currency_id: formData.foreign_currency_id || undefined,
        exchange_rate: exchangeRate,
        payment_term_id: formData.payment_term_id || undefined,
        purchase_order_id: formData.purchase_order_id || undefined,
        total_amount_base: finalTotal,
        subtotal_base: taxableTotal,
        discount_amount_base: discountAmount,
        tax_amount_base: taxTotal,
        cgst_amount_base: cgstTotal,
        sgst_amount_base: sgstTotal,
        igst_amount_base: igstTotal,
        ugst_amount_base: ugstTotal,
        cess_amount_base: cessTotal,
        subtotal_foreign: subtotalForeign,
        discount_amount_foreign: discountAmountForeign,
        tax_amount_foreign: taxAmountForeign,
        total_amount_foreign: finalTotalForeign,
        notes: formData.notes || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        items: validItems.map((item, index) => ({
          line_no: index + 1,
          product_id: item.product_id,
          product_name: item.product_name || '',
          quantity: item.quantity,
          free_quantity: item.free_quantity || 0,
          uom: 'NOS',
          unit_price_base: item.unit_price,
          unit_price_foreign: item.unit_price_foreign ? item.unit_price_foreign : item.unit_price * exchangeRate,
          discount_percent: item.discount_percent,
          discount_amount_base: item.line_discount_amount,
          discount_amount_foreign: (item.line_discount_amount || 0) * exchangeRate,
          taxable_amount_base: item.taxable_amount,
          taxable_amount_foreign: (item.taxable_amount || 0) * exchangeRate,
          cgst_rate: item.cgst_rate,
          cgst_amount_base: item.cgst_amount,
          sgst_rate: item.sgst_rate,
          sgst_amount_base: item.sgst_amount,
          igst_rate: item.igst_rate,
          igst_amount_base: item.igst_amount,
          ugst_rate: item.ugst_rate,
          ugst_amount_base: item.ugst_amount,
          cess_rate: item.cess_rate,
          cess_amount_base: item.cess_amount,
          tax_amount_base: item.total_tax_amount,
          tax_amount_foreign: (item.total_tax_amount || 0) * exchangeRate,
          total_amount_base: item.total_amount,
          total_amount_foreign: (item.total_amount || 0) * exchangeRate,
          hsn_code: item.hsn_code || undefined,
          batch_number: item.batch_number || undefined,
          description: item.description || undefined
        }))
      };

      const roundedInvoiceData = roundAmountFields(invoiceData);
      
      // Ensure supplier details are present
      if (!roundedInvoiceData.supplier_name && formData.supplier_name) {
        roundedInvoiceData.supplier_name = formData.supplier_name;
      }
      if (!roundedInvoiceData.supplier_phone && formData.supplier_phone) {
        roundedInvoiceData.supplier_phone = formData.supplier_phone;
      }
      
      // Log the data being sent
      console.log('Sending invoice data to API:', roundedInvoiceData);
      
      if (editData) {
        // Update existing invoice
        await purchaseInvoiceService.updatePurchaseInvoice(editData.id, roundedInvoiceData);
      } else {
        // Create new invoice
        await purchaseInvoiceService.createPurchaseInvoice(roundedInvoiceData);
      }
      onSave();
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to save invoice';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
      }
      
      showToast('error', errorMessage);
    }
  };

  const { subtotal, discountAmount, finalTotal, subtotalForeign, taxAmountForeign, discountAmountForeign, finalTotalForeign } = calculateTotals();

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="border-b border-gray-200 flex justify-between items-center erp-section-header">
        <h2 className="font-medium text-gray-800 erp-section-title">Create Purchase Invoice</h2>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="text-gray-500 hover:text-gray-700"
        >
          {isCollapsed ? <ChevronDown className="erp-form-btn-icon" style={{ marginRight: 0 }} /> : <ChevronUp className="erp-form-btn-icon" style={{ marginRight: 0 }} />}
        </button>
      </div>

      {!isCollapsed && (
        <form onSubmit={handleSubmit} className="erp-form-body">
          <div className="grid grid-cols-1 sm:grid-cols-6 erp-form-grid">
            <div>
              <label className="erp-form-label">Invoice Number</label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                className="w-full erp-form-input bg-gray-100"
                readOnly
              />
            </div>

            <div>
              <label className="erp-form-label">Supplier *</label>
              <SearchableDropdown
                options={suppliers.map(s => ({ value: s.id.toString(), label: `${s.phone} | ${s.name}` }))}
                value={formData.supplier_id}
                onChange={(value) => handleSupplierChange(value)}
                placeholder="Select supplier..."
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="erp-form-label">Warehouse</label>
              <SearchableDropdown
                options={warehouses.map(warehouse => ({
                  value: warehouse.id.toString(),
                  label: warehouse.name
                }))}
                value={formData.warehouse_id}
                onChange={(value) => setFormData({ ...formData, warehouse_id: value.toString() })}
                placeholder="Select warehouse..."
                multiple={false}
                searchable={true}
                className="w-full"
              />
            </div>

            <div>
              <label className="erp-form-label">Invoice Date *</label>
              <DatePicker
                value={formData.invoice_date}
                onChange={(value) => setFormData({ ...formData, invoice_date: value })}
                placeholder="Select date"
                className="w-full"
              />
            </div>

            <div>
              <label className="erp-form-label">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full erp-form-input focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="DRAFT">DRAFT</option>
                <option value="POSTED">POSTED</option>
              </select>
            </div>

            <div>
              <label className="erp-form-label">Due Date</label>
              <DatePicker
                value={formData.due_date}
                onChange={(value) => setFormData({ ...formData, due_date: value })}
                placeholder="Select due date"
                className="w-full"
              />
            </div>
          </div>

          <div className="erp-form-section-header flex justify-between items-center">
            <h3 className="erp-form-subsection-title">Invoice Items</h3>
            <button
              type="button"
              onClick={addItem}
              className="erp-form-btn erp-btn-add-item"
            >
              <Plus className="erp-form-btn-icon" />
              Add Item
            </button>
          </div>

          <div className="mb-6">
            <style>{`
              .purchase-invoice-table { table-layout: fixed !important; }
              .purchase-invoice-table th:nth-child(1), .purchase-invoice-table td:nth-child(1) { width: 15% !important; }
              .purchase-invoice-table th:nth-child(2), .purchase-invoice-table td:nth-child(2) { width: 8% !important; }
              .purchase-invoice-table th:nth-child(3), .purchase-invoice-table td:nth-child(3) { width: 8% !important; }
              .purchase-invoice-table th:nth-child(4), .purchase-invoice-table td:nth-child(4) { width: 7% !important; }
              .purchase-invoice-table th:nth-child(5), .purchase-invoice-table td:nth-child(5) { width: 7% !important; }
              .purchase-invoice-table th:nth-child(6), .purchase-invoice-table td:nth-child(6) { width: 6% !important; }
              .purchase-invoice-table th:nth-child(7), .purchase-invoice-table td:nth-child(7) { width: 6% !important; }
              .purchase-invoice-table th:nth-child(8), .purchase-invoice-table td:nth-child(8) { width: 7% !important; }
              .purchase-invoice-table th:nth-child(9), .purchase-invoice-table td:nth-child(9) { width: 7% !important; }
              .purchase-invoice-table th:nth-child(10), .purchase-invoice-table td:nth-child(10) { width: 8% !important; }
              .purchase-invoice-table th:nth-child(11), .purchase-invoice-table td:nth-child(11) { width: 5% !important; }
            `}</style>
            <div className="overflow-x-auto">
              <table className="border border-gray-200 purchase-invoice-table" style={{ minWidth: '1200px', width: '100%' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-left">Product</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">HSN</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Batch</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">MRP</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Price</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Qty</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Free</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Disc%</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">GST%</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Total</th>
                    <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-2 py-2" style={{ minWidth: '180px' }}>
                        <SearchableDropdown
                          options={products.map(p => ({ value: p.id.toString(), label: p.name }))}
                          value={item.product_id.toString()}
                          onChange={(value) => handleProductChange(index, value)}
                          placeholder="Select product..."
                          multiple={false}
                          searchable={true}
                          className="w-full"
                        />
                      </td>
                      <td className="px-3 py-2 w-20 text-sm text-center">{item.hsn_code || '-'}</td>
                      <td className="px-3 py-2 w-20">
                        <input
                          type="text"
                          value={item.batch_number || ''}
                          onChange={(e) => handleItemChange(index, 'batch_number', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          placeholder="Batch"
                        />
                      </td>
                      <td className="px-3 py-2 w-20 text-sm text-center">{toSafeFixed(item.mrp, 2)}</td>
                      <td className="px-3 py-2 w-20">
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-3 py-2 w-16">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-3 py-2 w-16">
                        <input
                          type="number"
                          value={item.free_quantity}
                          onChange={(e) => handleItemChange(index, 'free_quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-3 py-2 w-16">
                        <input
                          type="number"
                          value={item.discount_percent}
                          onChange={(e) => handleItemChange(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step="0.1"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-3 py-2 w-16 text-sm text-center">{toSafeFixed(item.gst_rate, 2)}</td>
                      <td className="px-3 py-2 w-24 text-sm text-center">{toSafeFixed(item.total_amount, 2)}</td>
                      <td className="px-3 py-2 w-16 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800"
                          disabled={items.length === 1}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end">
            <div className="w-full md:w-1/2 erp-summary-container">
              <h4 className="font-semibold" style={{ marginBottom: 'var(--erp-spacing-lg)' }}>Invoice Summary</h4>
              <div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Subtotal:</span>
                  <span className="erp-summary-value">{toSafeFixed(subtotal, 2)}</span>
                </div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Discount %:</span>
                  <input
                    type="number"
                    name="discount_percent"
                    value={formData.discount_percent}
                    onChange={handleInputChange}
                    className="erp-summary-input"
                    step="0.1"
                  />
                </div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Discount Amount:</span>
                  <input
                    type="number"
                    name="discount_amount"
                    value={formData.discount_amount || 0}
                    onChange={handleInputChange}
                    className="erp-summary-input"
                    step="0.01"
                  />
                </div>
                <div className="erp-summary-row hidden">
                  <span className="erp-summary-label">Round Off:</span>
                  <input
                    type="number"
                    name="roundoff"
                    value={formData.roundoff}
                    onChange={handleInputChange}
                    className="erp-summary-input"
                    step="0.01"
                  />
                </div>
                <div className="erp-summary-row erp-summary-total">
                  <span className="erp-summary-label">Total:</span>
                  <span className="erp-summary-value">{toSafeFixed(finalTotal, 2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end" style={{ gap: 'var(--erp-spacing-sm)', marginTop: 'var(--erp-spacing-xl)' }}>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="erp-form-btn text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="erp-form-btn text-white bg-primary hover:bg-secondary"
            >
              {editData ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PurchaseInvoiceForm;
